import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, X, Send, SkipForward } from "lucide-react";

interface ReflectionDialogProps {
    isOpen: boolean;
    taskName: string;
    onSubmit: (text: string) => void;
    onSkip: () => void;
}

export function ReflectionDialog({
    isOpen,
    taskName,
    onSubmit,
    onSkip,
}: ReflectionDialogProps) {
    const [text, setText] = useState("");

    if (!isOpen) return null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (text.trim()) {
            onSubmit(text.trim());
            setText("");
        }
    };

    const handleSkip = () => {
        setText("");
        onSkip();
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md border-0 shadow-2xl">
                <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                <MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            Reflection Time
                        </CardTitle>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleSkip}
                            className="h-8 w-8"
                        >
                            <X className="w-4 h-4" />
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="text-sm text-muted-foreground">
                        Working on:{" "}
                        <span className="font-medium text-foreground">
                            {taskName}
                        </span>
                    </div>

                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
                        <p className="text-sm font-medium mb-1">
                            What have you accomplished so far?
                        </p>
                        <p className="text-xs text-muted-foreground">
                            Take a moment to note your progress. This helps you
                            stay focused and track your work.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-3">
                        <Input
                            placeholder="e.g., Finished the header section..."
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            className="h-11"
                            autoFocus
                        />
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleSkip}
                                className="flex-1"
                            >
                                <SkipForward className="w-4 h-4 mr-2" />
                                Skip
                            </Button>
                            <Button
                                type="submit"
                                disabled={!text.trim()}
                                className="flex-1"
                            >
                                <Send className="w-4 h-4 mr-2" />
                                Save & Continue
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
